import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Box, Grid, Typography } from "@mui/material";
import ArrowBackIosRoundedIcon from "@mui/icons-material/ArrowBackIosRounded";
import {
  clearEmployeeDetails,
  getEmployeeDetails,
  updateEmployee,
  addEmployee,
} from "redux/actions/EmployeesAction";
import { fetchCompanyDetails } from "redux/actions/companyAction";
import { getCountryCode } from "redux/actions/authAction";
import EmployeeMultiForm from "components/employeeMultiForm/EmployeeMultiForm";
import CommonButton from "components/commonButton/CommonButton";
import Loader from "components/loader/Loader";
import { addingEmployeeForm } from "./EmployeeData";

const APPROVAL_STATUS_OPTIONS = [
  { label: "Pending", value: "Pending" },
  { label: "Approved", value: "Approved" },
  { label: "Rejected", value: "Rejected" },
];

const NewEmployee = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const isEditMode = location?.state?.type === "edit";

  const userProfile = useSelector(
    (state) => state?.userProfile?.profileDetails?.data,
  );

  const details = useSelector(
    (state) => state?.getEmployeeDetails?.employeeDetails?.data,
  );

  const employeeDetailsLoader = useSelector(
    (state) => state?.getEmployeeDetails?.loading,
  );

  const selectedCompanyDetails = useSelector(
    (state) => state?.userCompanyDetails?.companyDetails,
  );

  const selectedCompanyLoader = useSelector(
    (state) => state?.userCompanyDetails?.loading,
  );

  const countryCodeListResponse = useSelector((state) => state?.getCountryCode);
  const countryCodeListData =
    countryCodeListResponse?.countryCodeList?.data?.map((data) => {
      return { label: `${data?.code}`, value: `+${data?.dialCode}` };
    });

  const [edit, setEdit] = useState(false);

  const role = localStorage?.getItem("role");

  const companyID =
    localStorage.getItem("subCompany")?.length > 0 &&
    JSON.parse(localStorage.getItem("subCompany"));

  const selectedCompanyFromDropdown = location?.state?.selectedCompany;

  const finalCompanyId = companyID?.value || selectedCompanyFromDropdown?.value;

  const branchOption =
    selectedCompanyDetails?.branch?.map((val) => ({
      value: val?.id,
      label: val?.address,
    })) || [];

  // Separate defaults for ADD mode
  const addModeDefaults = {
    employeeType: "newemployee",
    approvalStatus: "Pending",
  };

  //FIX: Clean "NA" and null values so the input fields stay blank
  const cleanValue = (val) => {
    if (!val || val === "NA" || val === "null" || val === "undefined") {
      return "";
    }
    return val;
  };

  // Separate defaults for EDIT mode
  const editModeDefaults = {
    companyEmployeeEmail: cleanValue(details?.email),
    name: cleanValue(details?.name),
    countryCode: cleanValue(details?.countryCode),
    phoneNumber: cleanValue(details?.phoneNumber),
    employeeId: cleanValue(details?.employeeId),
    code: cleanValue(details?.code),
    role: details?.role,
    branchName: details?.branchId,
    billingTypeAccess: details?.billingTypeAccess,
    status: details?.status,
    approvalStatus: details?.approvalStatus || "Pending",
    employeeType: "newemployee",
  };

  // Use appropriate defaults based on mode
  const defaultValues = isEditMode ? editModeDefaults : addModeDefaults;

  useEffect(() => {
    if (finalCompanyId) {
      dispatch(fetchCompanyDetails({ id: finalCompanyId, type: "newBooking" }));
    }
    dispatch(getCountryCode());
  }, [finalCompanyId]);

  useEffect(() => {
    if (location?.state?.id) {
      dispatch(getEmployeeDetails(location?.state?.id));
      setEdit(true); // Starts in locked/view mode
    }
    return () => {
      dispatch(clearEmployeeDetails());
    };
  }, [location?.state?.id]);

  const navigateBack = () => {
    if (selectedCompanyFromDropdown?.value) {
      navigate(
        `/employees?companyId=${selectedCompanyFromDropdown.value}&companyName=${encodeURIComponent(
          selectedCompanyFromDropdown.label,
        )}`,
        { state: { selectedCompany: selectedCompanyFromDropdown } },
      );
    } else {
      navigate("/employees");
    }
  };

  const formSubmit = (data, country) => {
    // 1. Build base payload with mandatory fields
    const employeeData = {
      companyId: finalCompanyId,
      name: data?.name,
      countryCode: country,
      phoneNumber: data?.phoneNumber,
      email: data?.companyEmployeeEmail,
      role: data?.role,
      branchId: data?.branchName,
      billingTypeAccess: Number(data?.billingTypeAccess),
      approvalStatus: data?.approvalStatus || "Pending",
    };

    if (isEditMode) {
      employeeData.employeeId = data?.employeeId
        ? String(data.employeeId).trim()
        : "";
      employeeData.code = data?.code ? String(data.code).trim() : "";
    } else {
      // ADD MODE: Omit them entirely if empty to prevent 400 Bad Request
      if (data?.employeeId && String(data.employeeId).trim() !== "") {
        employeeData.employeeId = String(data.employeeId).trim();
      }

      if (data?.code && String(data.code).trim() !== "") {
        employeeData.code = String(data.code).trim();
      }
    }

    // 3. Dispatch based on mode
    if (isEditMode) {
      dispatch(updateEmployee(employeeData, details?.id, navigateBack));
    } else {
      dispatch(addEmployee(employeeData, navigateBack));
    }
  };

  const handleNavigate = () => {
    navigateBack();
  };

  const pageTitle = isEditMode ? "Employee Details" : "Enter Employee Details";

  if (employeeDetailsLoader || selectedCompanyLoader) {
    return <Loader height={"60vh"} />;
  }

  return (
    <Grid container justifyContent={"center"} className="bgLightBlue br20">
      <Grid item xs={11} lg={11} className="ptb30">
        <Grid container justifyContent={"space-between"}>
          <Box
            className="pb20"
            sx={{
              display: "flex",
              alignItems: "center",
            }}
          >
            <ArrowBackIosRoundedIcon
              className="font24 colorGrey mr10"
              role="presentation"
              onClick={handleNavigate}
              sx={{ cursor: "pointer" }}
            />

            <Typography
              variant="head3"
              component="h1"
              className="colorBlack fw600"
            >
              {pageTitle}
            </Typography>
          </Box>

          {isEditMode && role === "adminCompany" && (
            <CommonButton
              name={edit ? "Edit" : "Cancel"}
              iconType={edit ? "edit" : "close"}
              iconPlace="front"
              btnClass={`btnSm ${edit ? "btnEdit" : "btnDelete"}`}
              onClick={() => setEdit(!edit)}
            />
          )}
        </Grid>

        {/* ADD MODE */}
        {!isEditMode && (
          <EmployeeMultiForm
            key="add-employee"
            formData={addingEmployeeForm(
              branchOption,
              [],
              APPROVAL_STATUS_OPTIONS,
            )}
            onSubmittBtn={formSubmit}
            button={"Submit"}
            userProfile={userProfile}
            defaultValues={addModeDefaults}
            countryCodeListData={countryCodeListData}
          />
        )}

        {/* EDIT MODE */}
        {isEditMode && (
          <EmployeeMultiForm
            key={details?.id || "edit-employee"}
            formData={addingEmployeeForm(
              branchOption,
              [],
              APPROVAL_STATUS_OPTIONS,
            )}
            onSubmittBtn={formSubmit}
            button={!edit ? "Submit" : ""}
            userProfile={userProfile}
            defaultValues={editModeDefaults}
            disabled={edit}
            countryCodeListData={countryCodeListData}
          />
        )}
      </Grid>
    </Grid>
  );
};

export default NewEmployee;
